import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Container, Typography, Grid, Card, CardContent } from '@material-ui/core';
import { Code, School, Laptop, CheckCircle } from '@material-ui/icons';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #1a1a1a;
  color: #e5e5e5;
`;

const HeroSection = styled.div`
  padding: 80px 20px;
  text-align: center;
  background: linear-gradient(145deg, #2a2a2a, #1f1f1f);
  border-bottom: 2px solid #4a4a4a;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: #e5e5e5;
  margin-bottom: 20px;
  text-shadow: 0 0 20px rgba(139, 92, 246, 0.3);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: #b0b0b0;
  margin-bottom: 40px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #8b5cf6, #6366f1);
    color: white;
    padding: 15px 40px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 8px;
    text-transform: none;
    box-shadow: 0 0 20px rgba(139, 92, 246, 0.6);
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #a78bfa, #818cf8);
      box-shadow: 0 0 30px rgba(139, 92, 246, 0.8);
      transform: translateY(-2px);
    }
  }
`;

const Section = styled.div`
  padding: 60px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #e5e5e5;
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const FeatureCard = styled(Card)`
  && {
    background: linear-gradient(145deg, #2a2a2a, #1f1f1f);
    border: 1px solid #4a4a4a;
    border-radius: 12px;
    height: 100%;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 0 20px rgba(100, 100, 255, 0.3);
      border-color: #6464ff;
    }
  }
`;

const FeatureIcon = styled.div`
  color: #8b5cf6;
  margin-bottom: 15px;

  svg {
    font-size: 3rem;
    filter: drop-shadow(0 0 10px rgba(139, 92, 246, 0.5));
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  color: #e5e5e5;
  margin-bottom: 10px;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: #b0b0b0;
  line-height: 1.6;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  svg {
    color: #6464ff;
    margin-right: 15px;
    font-size: 1.5rem;
    flex-shrink: 0;
  }
`;

const BenefitText = styled.p`
  color: #d0d0d0;
  font-size: 1.1rem;
  line-height: 1.6;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Home = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Mark that user has visited the homepage
    localStorage.setItem('hasVisitedHome', 'true');
  }, []);

  const handleGetStarted = () => {
    navigate('/payment');
  };

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Learn Programming from Expert Tutors</HeroTitle>
        <HeroSubtitle>
          One-on-one online coding tutoring sessions designed for young learners. Master programming concepts, build real projects, and develop
          problem-solving skills.
        </HeroSubtitle>
        <CTAButton onClick={handleGetStarted}>Get Started Today</CTAButton>
      </HeroSection>

      <Section>
        <SectionTitle>Why Choose Our Tutoring?</SectionTitle>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <FeatureCard>
              <CardContent style={{ padding: '30px', color: '#e5e5e5' }}>
                <FeatureIcon>
                  <Code />
                </FeatureIcon>
                <FeatureTitle>Expert Instructors</FeatureTitle>
                <FeatureDescription>
                  Learn from experienced programmers who specialize in teaching young students. Our tutors break down complex concepts into
                  easy-to-understand lessons.
                </FeatureDescription>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard>
              <CardContent style={{ padding: '30px', color: '#e5e5e5' }}>
                <FeatureIcon>
                  <Laptop />
                </FeatureIcon>
                <FeatureTitle>Interactive Learning</FeatureTitle>
                <FeatureDescription>
                  Hands-on coding sessions via video call with screen sharing. Work on real projects and get instant feedback on your code.
                </FeatureDescription>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard>
              <CardContent style={{ padding: '30px', color: '#e5e5e5' }}>
                <FeatureIcon>
                  <School />
                </FeatureIcon>
                <FeatureTitle>Flexible Scheduling</FeatureTitle>
                <FeatureDescription>
                  Book sessions that fit your schedule. Learn at your own pace with personalized curriculum tailored to your skill level and goals.
                </FeatureDescription>
              </CardContent>
            </FeatureCard>
          </Grid>
        </Grid>
      </Section>

      <Section style={{ background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)', margin: '0', maxWidth: '100%' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <SectionTitle>What You'll Learn</SectionTitle>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <BenefitItem>
                <CheckCircle />
                <BenefitText>
                  <strong>Programming Fundamentals:</strong> Variables, loops, functions, and data structures
                </BenefitText>
              </BenefitItem>
              <BenefitItem>
                <CheckCircle />
                <BenefitText>
                  <strong>Popular Languages:</strong> Python, JavaScript, Java, and more
                </BenefitText>
              </BenefitItem>
              <BenefitItem>
                <CheckCircle />
                <BenefitText>
                  <strong>Web Development:</strong> HTML, CSS, and building interactive websites
                </BenefitText>
              </BenefitItem>
            </Grid>
            <Grid item xs={12} md={6}>
              <BenefitItem>
                <CheckCircle />
                <BenefitText>
                  <strong>Problem Solving:</strong> Algorithms, debugging, and logical thinking
                </BenefitText>
              </BenefitItem>
              <BenefitItem>
                <CheckCircle />
                <BenefitText>
                  <strong>Real Projects:</strong> Build games, apps, and practical applications
                </BenefitText>
              </BenefitItem>
              <BenefitItem>
                <CheckCircle />
                <BenefitText>
                  <strong>Computer Science:</strong> Core concepts for academic success
                </BenefitText>
              </BenefitItem>
            </Grid>
          </Grid>
        </div>
      </Section>

      <Section>
        <div style={{ textAlign: 'center' }}>
          <SectionTitle>Ready to Start Your Coding Journey?</SectionTitle>
          <Typography style={{ color: '#b0b0b0', fontSize: '1.2rem', marginBottom: '30px' }}>
            Choose a package that works for you and schedule your first session today!
          </Typography>
          <CTAButton onClick={handleGetStarted}>View Packages & Book Now</CTAButton>
        </div>
      </Section>

      <div style={{ textAlign: 'center', padding: '40px 20px', borderTop: '1px solid #4a4a4a' }}>
        <Typography style={{ color: '#909090', fontSize: '0.9rem' }}>
          © 2025 CrystalSecurePay. All payments processed securely through Stripe.
        </Typography>
      </div>
    </PageContainer>
  );
};

export default Home;
